import {
	createActionsHook,
	createHook,
	createStateHook,
	createStore,
	type BoundActions,
	type HookActionsFunction,
	type HookFunction,
	type HookStateFunction,
} from 'react-sweet-state';

import type { InteractionSource } from '@atlassian/conversation-assistant-instrumentation/constants/analytics';

// The entry point that triggered the staging area.
export type StagingAreaEntryPoint =
	| 'globalCreateDropdown-smartCreate'
	| 'spaceLevelContextualCreate-smartCreate'
	| 'pageTreeItemContextualCreate-smartCreate'
	| 'endOfContentTreeContextualCreate-smartCreate'
	| 'poConfluenceSideNavCreate'
	| 'whiteboardToLiveDocAiConversion'
	| 'rovoChatPreviewCard'
	| 'rovoChatArtifactPreview'
	| 'jiraCreateWithAIDropdown'
	| 'jsmFormsRovoCreate'
	| 'rovoChat'
	| 'dynamicUi'
	| 'dynamicUiFromInlineEditorToolbarAskRovo'
	| 'template-smartCreate'
	| 'aifc-onboarding-confluence';

export type StagingAreaMode = 'inline' | 'modal' | 'ephemeralModal' | undefined;

type StagingAreaState = {
	/**
	 * ID of the LLM generated content that is being previewed in the staging area.
	 */
	messageIdSelectedForPreview: string | null;
	/**
	 * Invocation ID of the LLM generated content that is being previewed in the staging area.
	 */
	invocationIdSelectedForPreview: string | null;
	/**
	 * Indicates the state of the **Platform-mounted** StagingAreaModal.
	 * - 'inline' - Used when opening the StagingArea within Atlassian Home.  It displays beside Rovo Chat, not in a modal.
	 * - 'modal' - Used when the standard full-screen StagingAreaModal is mounted from a Rovo Chat entrypoint, like the "Expand" button in preview cards.
	 * - 'ephemeralModal' - Used when the Platform-mounted Remix Ephemeral Preview version of the StagingAreaModal **is mounted from a Rovo Chat entrypoint**.
	 * - undefined - Used when no Platform-mounted StagingAreaModal is visible.
	 *
	 * WARNING: The StagingAreaModal can also be mounted at other locations besides the Platform mount point within ConversationAssistant.
	 * When these alternate mount points are used, `stagingAreaMode` may be `undefined`.
	 * i.e. For general Ephemeral Preview checks, use the `isFloating` prop instead.
	 */
	stagingAreaMode: StagingAreaMode;

	stagingAreaEntryPoint: StagingAreaEntryPoint | '';
	/** String representation of the user's prompt  */
	prompt: string;
	/**
	 * Knowledge-source keys for the 3P connectors tied to a selected suggested
	 * prompt, carried from the Create-with-Rovo modal into chat so the chat can
	 * surface the app-connection banner for them. Empty for non-3P prompts.
	 */
	promptAppConnectorSources: string[];
	/**
	 * True when the staged prompt came from a third-party suggested prompt tile, whatever
	 * connectors it carried. Stored rather than derived from `promptAppConnectorSources` because
	 * a 3P tile can arrive with no connectors at all, and an empty source list must still be
	 * attributed to the experiment (CCINT-8714).
	 */
	isThirdPartyPromptStaged: boolean;
	/**
	 * The dynamic UI type selected when the remix flow was initiated (e.g. 'map', 'infographic').
	 * Stored so that EditWithRovoBar can initialise with the correct variant.
	 */
	dynamicUiType: string | undefined;
	/**
	 * The dynamic UI subtype selected when the remix flow was initiated (e.g. 'line-chart', 'mindmap').
	 * Stored so that experience tracking attributes can include it.
	 */
	dynamicUiSubtype: string | undefined;
	/**
	 * The subtype the user picked at initiation (e.g. 'gantt-chart'). Unlike
	 * `dynamicUiSubtype`, it isn't overwritten by the backend response, so
	 * EditWithRovoBar can detect an override. Cleared after the flag is shown.
	 */
	userSelectedDynamicUiSubtype: string | undefined;
	/**
	 * The dynamic UI source context captured when the remix flow was initiated.
	 * Stored so that EditWithRovoBar can forward it on remix adjustment messages.
	 */
	dynamicUiSource: unknown;
	/**
	 * The interaction source that triggered the remix flow (e.g. 'remixModalFromObjectLevelSidebar').
	 * Stored so that it can be included in Remix feedback metadata.
	 */
	interactionSource: InteractionSource | undefined;
	/**
	 * True while the EditWithRovoBar is waiting for a remix adjustment response.
	 * Used by DynamicUiContentOverlay to keep the loading overlay visible
	 * until the new MAUI app is ready, preventing a flash of the CWR loading state.
	 */
	isRemixUpdating: boolean;
	/**
	 * If true, it will prevent the conversation store from automatically selecting a conversation
	 * based on URL params or local storage.
	 */
	forceSkipAutoConversationSelection: boolean;
	/**
	 * The message ID corresponding to the version the user is currently viewing
	 * in the undo/redo stack. When `undefined`, the latest message
	 * (`selectedPreviewMessage`) is assumed to be the displayed version.
	 *
	 * Set by the staging area's `onNavigate` callback when the user presses
	 * undo/redo, and reset to `undefined` when a new generation is pushed onto
	 * the history stack.
	 */
	currentDisplayMessageId: string | undefined;
	/**
	 * Closes the currently-mounted **Platform-mounted** StagingAreaModal, registered by
	 * `SmartCreationModalWithAnalytics` while it's mounted (and cleared on unmount). Lets any
	 * consumer close the modal — e.g. a preview card in a different `ConversationAssistant`
	 * instance entirely, rendered behind the modal — without needing product-specific wiring.
	 * `null` when no such modal is currently mounted.
	 */
	closeModalCallback: (() => void) | null;
};

const initialState: StagingAreaState = {
	messageIdSelectedForPreview: null,
	invocationIdSelectedForPreview: null,
	stagingAreaMode: undefined,
	stagingAreaEntryPoint: '',
	prompt: '',
	promptAppConnectorSources: [],
	isThirdPartyPromptStaged: false,
	dynamicUiType: undefined,
	dynamicUiSubtype: undefined,
	userSelectedDynamicUiSubtype: undefined,
	dynamicUiSource: undefined,
	interactionSource: undefined,
	isRemixUpdating: false,
	forceSkipAutoConversationSelection: false,
	currentDisplayMessageId: undefined,
	closeModalCallback: null,
};

const actions = {
	setMessageIdSelectedForPreview:
		(messageId: string | null) =>
		({ setState }: { setState: (state: Partial<StagingAreaState>) => void }): void => {
			setState({
				messageIdSelectedForPreview: messageId,
			});
		},
	getMessageIdSelectedForPreview:
		() =>
		({ getState }: { getState: () => StagingAreaState }): string | null => {
			return getState().messageIdSelectedForPreview;
		},
	setStagingAreaMode:
		(mode: StagingAreaMode, entryPoint: StagingAreaEntryPoint | undefined) =>
		({ setState }: { setState: (state: Partial<StagingAreaState>) => void }): void => {
			setState({
				stagingAreaMode: mode,
				stagingAreaEntryPoint: entryPoint ?? '',
			});
		},
	getStagingAreaEntryPoint:
		() =>
		({ getState }: { getState: () => StagingAreaState }): StagingAreaEntryPoint | '' =>
			getState().stagingAreaEntryPoint,
	setStagingAreaEntryPoint:
		(entryPoint: StagingAreaEntryPoint | undefined) =>
		({ setState }: { setState: (state: Partial<StagingAreaState>) => void }): void => {
			setState({
				stagingAreaEntryPoint: entryPoint ?? '',
			});
		},
	setPrompt:
		(prompt: string) =>
		({ setState }: { setState: (state: Partial<StagingAreaState>) => void }): void => {
			setState({
				prompt,
			});
		},
	/**
	 * Writes `isThirdPartyPromptStaged` in the same update, so the flag cannot drift from the
	 * source list. Omitting `isThirdPartyPrompt` derives it from the list, which is what the
	 * one-argument call has always meant: before this experiment a non-empty list was the only way
	 * to stage a 3P prompt. Pass it for a 3P tile that carried no connectors — an empty list the
	 * reader must still attribute to the experiment (CCINT-8714).
	 */
	setPromptAppConnectorSources:
		(promptAppConnectorSources: string[], options?: { isThirdPartyPrompt?: boolean }) =>
		({ setState }: { setState: (state: Partial<StagingAreaState>) => void }): void => {
			setState({
				promptAppConnectorSources,
				isThirdPartyPromptStaged:
					options?.isThirdPartyPrompt ?? promptAppConnectorSources.length > 0,
			});
		},
	setInvocationIdSelectedForPreview:
		(invocationId: string | null) =>
		({ setState }: { setState: (state: Partial<StagingAreaState>) => void }): void => {
			setState({
				invocationIdSelectedForPreview: invocationId,
			});
		},
	getInvocationIdSelectedForPreview:
		() =>
		({ getState }: { getState: () => StagingAreaState }): string | null => {
			return getState().invocationIdSelectedForPreview;
		},
	setIsRemixUpdating:
		(isUpdating: boolean) =>
		({ setState }: { setState: (state: Partial<StagingAreaState>) => void }): void => {
			setState({
				isRemixUpdating: isUpdating,
			});
		},
	setDynamicUiContext:
		(
			dynamicUiType: string | undefined,
			dynamicUiSubtype: string | undefined,
			dynamicUiSource?: unknown,
		) =>
		({ setState }: { setState: (state: Partial<StagingAreaState>) => void }): void => {
			setState({
				dynamicUiType,
				dynamicUiSubtype,
				dynamicUiSource,
			});
		},
	setUserSelectedDynamicUiSubtype:
		(userSelectedDynamicUiSubtype: string | undefined) =>
		({ setState }: { setState: (state: Partial<StagingAreaState>) => void }): void => {
			setState({
				userSelectedDynamicUiSubtype,
			});
		},
	setInteractionSource:
		(interactionSource: InteractionSource | undefined) =>
		({ setState }: { setState: (state: Partial<StagingAreaState>) => void }): void => {
			setState({
				interactionSource,
			});
		},
	resetStagingArea:
		() =>
		({ setState }: { setState: (state: Partial<StagingAreaState>) => void }): void => {
			setState({
				messageIdSelectedForPreview: null,
				invocationIdSelectedForPreview: null,
				isRemixUpdating: false,
				stagingAreaMode: undefined,
				stagingAreaEntryPoint: '',
				promptAppConnectorSources: [],
				isThirdPartyPromptStaged: false,
				dynamicUiType: undefined,
				dynamicUiSubtype: undefined,
				userSelectedDynamicUiSubtype: undefined,
				dynamicUiSource: undefined,
				interactionSource: undefined,
				forceSkipAutoConversationSelection: false,
				currentDisplayMessageId: undefined,
			});
		},
	setForceSkipAutoConversationSelection:
		(forceSkip: boolean) =>
		({ setState }: { setState: (state: Partial<StagingAreaState>) => void }): void => {
			setState({
				forceSkipAutoConversationSelection: forceSkip,
			});
		},
	setCurrentDisplayMessageId:
		(messageId: string | undefined) =>
		({ setState }: { setState: (state: Partial<StagingAreaState>) => void }): void => {
			setState({
				currentDisplayMessageId: messageId,
			});
		},
	setCloseModalCallback:
		(closeModalCallback: (() => void) | null) =>
		({ setState }: { setState: (state: Partial<StagingAreaState>) => void }): void => {
			setState({
				closeModalCallback,
			});
		},
	closeStagingAreaModal:
		() =>
		({ getState }: { getState: () => StagingAreaState }): void => {
			getState().closeModalCallback?.();
		},
};

const store = createStore({
	initialState,
	actions,
});

export const useMessageIdSelectedForPreview: HookFunction<
	string | null,
	BoundActions<
		StagingAreaState,
		{
			setMessageIdSelectedForPreview: (
				messageId: string | null,
			) => ({ setState }: { setState: (state: Partial<StagingAreaState>) => void }) => void;
			getMessageIdSelectedForPreview: () => ({
				getState,
			}: {
				getState: () => StagingAreaState;
			}) => string | null;
			setStagingAreaMode: (
				mode: StagingAreaMode,
				entryPoint: StagingAreaEntryPoint | undefined,
			) => ({ setState }: { setState: (state: Partial<StagingAreaState>) => void }) => void;
			getStagingAreaEntryPoint: () => ({
				getState,
			}: {
				getState: () => StagingAreaState;
			}) => StagingAreaEntryPoint | '';
			setStagingAreaEntryPoint: (
				entryPoint: StagingAreaEntryPoint | undefined,
			) => ({ setState }: { setState: (state: Partial<StagingAreaState>) => void }) => void;
			setPrompt: (
				prompt: string,
			) => ({ setState }: { setState: (state: Partial<StagingAreaState>) => void }) => void;
			setPromptAppConnectorSources: (
				promptAppConnectorSources: string[],
				options?: { isThirdPartyPrompt?: boolean },
			) => ({ setState }: { setState: (state: Partial<StagingAreaState>) => void }) => void;
			setInvocationIdSelectedForPreview: (
				invocationId: string | null,
			) => ({ setState }: { setState: (state: Partial<StagingAreaState>) => void }) => void;
			getInvocationIdSelectedForPreview: () => ({
				getState,
			}: {
				getState: () => StagingAreaState;
			}) => string | null;
			setIsRemixUpdating: (
				isUpdating: boolean,
			) => ({ setState }: { setState: (state: Partial<StagingAreaState>) => void }) => void;
			setDynamicUiContext: (
				dynamicUiType: string | undefined,
				dynamicUiSubtype: string | undefined,
				dynamicUiSource?: unknown,
			) => ({ setState }: { setState: (state: Partial<StagingAreaState>) => void }) => void;
			setUserSelectedDynamicUiSubtype: (
				userSelectedDynamicUiSubtype: string | undefined,
			) => ({ setState }: { setState: (state: Partial<StagingAreaState>) => void }) => void;
			setInteractionSource: (
				interactionSource: InteractionSource | undefined,
			) => ({ setState }: { setState: (state: Partial<StagingAreaState>) => void }) => void;
			resetStagingArea: () => ({
				setState,
			}: {
				setState: (state: Partial<StagingAreaState>) => void;
			}) => void;
			setForceSkipAutoConversationSelection: (
				forceSkip: boolean,
			) => ({ setState }: { setState: (state: Partial<StagingAreaState>) => void }) => void;
			setCurrentDisplayMessageId: (
				messageId: string | undefined,
			) => ({ setState }: { setState: (state: Partial<StagingAreaState>) => void }) => void;
			setCloseModalCallback: (
				closeModalCallback: (() => void) | null,
			) => ({ setState }: { setState: (state: Partial<StagingAreaState>) => void }) => void;
			closeStagingAreaModal: () => ({ getState }: { getState: () => StagingAreaState }) => void;
		}
	>,
	void
> = createHook(store, {
	selector: (state: StagingAreaState) => state.messageIdSelectedForPreview,
});

export const useStagingArea: HookFunction<
	StagingAreaState,
	BoundActions<
		StagingAreaState,
		{
			setMessageIdSelectedForPreview: (
				messageId: string | null,
			) => ({ setState }: { setState: (state: Partial<StagingAreaState>) => void }) => void;
			getMessageIdSelectedForPreview: () => ({
				getState,
			}: {
				getState: () => StagingAreaState;
			}) => string | null;
			setStagingAreaMode: (
				mode: StagingAreaMode,
				entryPoint: StagingAreaEntryPoint | undefined,
			) => ({ setState }: { setState: (state: Partial<StagingAreaState>) => void }) => void;
			getStagingAreaEntryPoint: () => ({
				getState,
			}: {
				getState: () => StagingAreaState;
			}) => StagingAreaEntryPoint | '';
			setStagingAreaEntryPoint: (
				entryPoint: StagingAreaEntryPoint | undefined,
			) => ({ setState }: { setState: (state: Partial<StagingAreaState>) => void }) => void;
			setPrompt: (
				prompt: string,
			) => ({ setState }: { setState: (state: Partial<StagingAreaState>) => void }) => void;
			setPromptAppConnectorSources: (
				promptAppConnectorSources: string[],
				options?: { isThirdPartyPrompt?: boolean },
			) => ({ setState }: { setState: (state: Partial<StagingAreaState>) => void }) => void;
			setInvocationIdSelectedForPreview: (
				invocationId: string | null,
			) => ({ setState }: { setState: (state: Partial<StagingAreaState>) => void }) => void;
			getInvocationIdSelectedForPreview: () => ({
				getState,
			}: {
				getState: () => StagingAreaState;
			}) => string | null;
			setIsRemixUpdating: (
				isUpdating: boolean,
			) => ({ setState }: { setState: (state: Partial<StagingAreaState>) => void }) => void;
			setDynamicUiContext: (
				dynamicUiType: string | undefined,
				dynamicUiSubtype: string | undefined,
				dynamicUiSource?: unknown,
			) => ({ setState }: { setState: (state: Partial<StagingAreaState>) => void }) => void;
			setUserSelectedDynamicUiSubtype: (
				userSelectedDynamicUiSubtype: string | undefined,
			) => ({ setState }: { setState: (state: Partial<StagingAreaState>) => void }) => void;
			setInteractionSource: (
				interactionSource: InteractionSource | undefined,
			) => ({ setState }: { setState: (state: Partial<StagingAreaState>) => void }) => void;
			resetStagingArea: () => ({
				setState,
			}: {
				setState: (state: Partial<StagingAreaState>) => void;
			}) => void;
			setForceSkipAutoConversationSelection: (
				forceSkip: boolean,
			) => ({ setState }: { setState: (state: Partial<StagingAreaState>) => void }) => void;
			setCurrentDisplayMessageId: (
				messageId: string | undefined,
			) => ({ setState }: { setState: (state: Partial<StagingAreaState>) => void }) => void;
			setCloseModalCallback: (
				closeModalCallback: (() => void) | null,
			) => ({ setState }: { setState: (state: Partial<StagingAreaState>) => void }) => void;
			closeStagingAreaModal: () => ({ getState }: { getState: () => StagingAreaState }) => void;
		}
	>,
	void
> = createHook(store);
export const useStagingAreaActions: HookActionsFunction<
	BoundActions<
		StagingAreaState,
		{
			setMessageIdSelectedForPreview: (
				messageId: string | null,
			) => ({ setState }: { setState: (state: Partial<StagingAreaState>) => void }) => void;
			getMessageIdSelectedForPreview: () => ({
				getState,
			}: {
				getState: () => StagingAreaState;
			}) => string | null;
			setStagingAreaMode: (
				mode: StagingAreaMode,
				entryPoint: StagingAreaEntryPoint | undefined,
			) => ({ setState }: { setState: (state: Partial<StagingAreaState>) => void }) => void;
			getStagingAreaEntryPoint: () => ({
				getState,
			}: {
				getState: () => StagingAreaState;
			}) => StagingAreaEntryPoint | '';
			setStagingAreaEntryPoint: (
				entryPoint: StagingAreaEntryPoint | undefined,
			) => ({ setState }: { setState: (state: Partial<StagingAreaState>) => void }) => void;
			setPrompt: (
				prompt: string,
			) => ({ setState }: { setState: (state: Partial<StagingAreaState>) => void }) => void;
			setPromptAppConnectorSources: (
				promptAppConnectorSources: string[],
				options?: { isThirdPartyPrompt?: boolean },
			) => ({ setState }: { setState: (state: Partial<StagingAreaState>) => void }) => void;
			setInvocationIdSelectedForPreview: (
				invocationId: string | null,
			) => ({ setState }: { setState: (state: Partial<StagingAreaState>) => void }) => void;
			getInvocationIdSelectedForPreview: () => ({
				getState,
			}: {
				getState: () => StagingAreaState;
			}) => string | null;
			setIsRemixUpdating: (
				isUpdating: boolean,
			) => ({ setState }: { setState: (state: Partial<StagingAreaState>) => void }) => void;
			setDynamicUiContext: (
				dynamicUiType: string | undefined,
				dynamicUiSubtype: string | undefined,
				dynamicUiSource?: unknown,
			) => ({ setState }: { setState: (state: Partial<StagingAreaState>) => void }) => void;
			setUserSelectedDynamicUiSubtype: (
				userSelectedDynamicUiSubtype: string | undefined,
			) => ({ setState }: { setState: (state: Partial<StagingAreaState>) => void }) => void;
			setInteractionSource: (
				interactionSource: InteractionSource | undefined,
			) => ({ setState }: { setState: (state: Partial<StagingAreaState>) => void }) => void;
			resetStagingArea: () => ({
				setState,
			}: {
				setState: (state: Partial<StagingAreaState>) => void;
			}) => void;
			setForceSkipAutoConversationSelection: (
				forceSkip: boolean,
			) => ({ setState }: { setState: (state: Partial<StagingAreaState>) => void }) => void;
			setCurrentDisplayMessageId: (
				messageId: string | undefined,
			) => ({ setState }: { setState: (state: Partial<StagingAreaState>) => void }) => void;
			setCloseModalCallback: (
				closeModalCallback: (() => void) | null,
			) => ({ setState }: { setState: (state: Partial<StagingAreaState>) => void }) => void;
			closeStagingAreaModal: () => ({ getState }: { getState: () => StagingAreaState }) => void;
		}
	>
> = createActionsHook(store);
export const useStagingAreaState: HookStateFunction<StagingAreaState, void> =
	createStateHook(store);
